import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import API from "../../api"
import HeaderComponent from "../../components/header/header.component"
import FooterComponent from "../../components/footer/footer.component"
import "./case-study.page.css"
import CaseStudyHeroComponent from "../../components/case-study-hero/case-study-hero.component"
import CaseStudyProblemSolutionComponent from "../../components/case-study-problem-solution/case-study-problem-solution.component"
import CaseStudyMediaContainerComponent from "../../components/case-study-media-container/case-study-media-container.component"
import CaseStudyResultsComponent from "../../components/case-study-results/case-study-results.component"
import WorkSliderComponent from "../../components/work-slider/work-slider.component"
import ImageSliderComponent from "../../components/image-slider/image-slider.component"
import useScrollToTop from "../../utils/scrollToTop.util"
import { daggerNavigate } from "../../utils/navigate.util"
import { Helmet } from "react-helmet"
import FlexContainerComponent from "../../components/flex-container/flex-container.component"
import PixelComponent from "../../components/pixel/pixel.component"
import SpacerComponent from "../../components/spacer/spacer.component"

const CaseStudy = () => {
  const { Slug } = useParams()
  const navigate = useNavigate()
  const [content, setContent] = useState()
  const [moreWork, setMoreWork] = useState()
  useScrollToTop(Slug)

  useEffect(() => {
    if (!Slug) return

    const getPage = async () => {
      const res = await API.getContent(
        "case-studies",
        null,
        {
          "filters[Slug][$eq]": Slug,
          "populate[0]": "PageContent",
          "populate[1]": "Category",
          "populate[2]": "PageContent.Items",
          "populate[3]": "PageContent.Media",
          "populate[4]": "PageContent.ProblemSolutions",
          "populate[5]": "PageContent.CaseStudyMedia.Media",
          "populate[6]": "PageContent.Results",
          "populate[7]": "PageContent.CaseStudyMedia.PhoneContainerAvatar",
          "populate[8]": "MetaImage.Media",
          "populate[9]": "PageContent.Items.Media",
        },
        true,
      )

      if (res) {
        setContent(res)
        setTimeout(
          () => document.dispatchEvent(new CustomEvent("EVENTS.STOP_LOADING")),
          250,
        )
      } else {
        // Todo: Show error
      }
    }

    getPage()
  }, [Slug])

  const getContentForComponent = (comp) => {
    switch (comp.__component) {
      default: {
        console.error("UI Component not found: ", comp.__component)
        return null
      }
      case "ui-components.dagger-case-study-hero": {
        return (
          <CaseStudyHeroComponent
            key={comp.__component + comp.id}
            {...comp}
            // HexColor={content.attributes.HexColor}
          />
        )
      }
      case "ui-components.dagger-case-study-problem-solution": {
        return (
          <CaseStudyProblemSolutionComponent
            key={comp.__component + comp.id}
            {...comp}
            // HexColor={content.attributes.HexColor}
          />
        )
      }
      case "ui-components.dagger-case-study-results": {
        return (
          <CaseStudyResultsComponent
            key={comp.__component + comp.id}
            {...comp}
            // HexColor={content.attributes.HexColor}
          />
        )
      }
      case "ui-components.dagger-case-study-media-container": {
        return (
          <CaseStudyMediaContainerComponent
            key={comp.__component + comp.id}
            {...comp}
            // HexColor={content.attributes.HexColor}
          />
        )
      }
      case "ui-components.dagger-image-slider": {
        return (
          <ImageSliderComponent
            key={comp.__component + comp.id}
            {...comp}
            useIntrinsicSize={Slug === "butter-atl-originals"}
          />
        )
      }
      case "ui-components.dagger-flex-container": {
        return (
          <FlexContainerComponent key={comp.__component + comp.id} {...comp} />
        )
      }
      case "ui-components.dagger-spacer": {
        return <SpacerComponent key={comp.__component + comp.id} {...comp} />
      }
    }
  }

  // Get more work on load
  useEffect(() => {
    const handleGetMoreWork = async () => {
      try {
        const workItems = await API.getContent("case-studies", null, {
          "filters[Slug][$not]": Slug,
          "populate[0]": "HeroImage",
        })

        let more = null
        if (workItems.length > 0) {
          // Shuffle so "More Work" isn't always the same order
          for (let i = workItems.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[workItems[i], workItems[j]] = [workItems[j], workItems[i]]
          }

          const items = workItems.map((w) => {
            return {
              CaseStudy: {
                data: w,
              },
              id: w.id,
            }
          })

          more = (
            <WorkSliderComponent
              Heading="More Work"
              Items={items}
              CtaButtonLabel="All Work"
            />
          )
        }

        setMoreWork(more)
      } catch (e) {
        // todo: show error
        console.error(e)
        return null
      }
    }

    if (content?.attributes.ShowMoreWork) {
      handleGetMoreWork()
    }
  }, [content, Slug])

  return (
    <div className="dagger-page">
      <Helmet>
        <title>{`DAGGER ${
          content?.attributes?.Title ? ` - ${content?.attributes?.Title}` : ""
        }`}</title>
        <meta
          name="description"
          content={content?.attributes?.MetaDescription}
        />
        <link
          rel="canonical"
          href={`https://${window.location.host}/study/${content?.attributes?.Slug}`}
        />
        <meta
          property="og:description"
          content={content?.attributes?.MetaDescription}
        />
        <meta property="og:site_name" content="DAGGER agency" />
        <meta property="og:title" content={content?.attributes?.Title} />
        <meta property="og:type" content={content?.attributes?.MetaPageType} />
        <meta
          property="og:image"
          content={
            content?.attributes.MetaImage?.data?.attributes?.url
              ? content.attributes.MetaImage.data.attributes.url
              : content?.attributes.MetaImageUrl
          }
        />
        <meta
          property="og:url"
          content={`https://${window.location.host}/study/${content?.attributes?.Slug}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={content?.attributes?.Title} />
        <meta
          name="twitter:description"
          content={content?.attributes?.MetaDescription}
        />
        <meta
          name="twitter:image"
          content={
            content?.attributes?.MetaImage?.data?.attributes?.url
              ? content.attributes?.MetaImage.data.attributes?.url
              : content?.attributes?.MetaImageUrl
          }
        />
        {content?.attributes?.Title && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CreativeWork",
              name: content.attributes.Title,
              description: content.attributes?.MetaDescription || "",
              url: `https://${window.location.host}/study/${content.attributes?.Slug}`,
              author: {
                "@type": "Organization",
                name: "DAGGER",
                url: "https://dagger.agency",
                logo: "https://dagger.agency/favicon.ico",
                sameAs: [
                  "https://www.instagram.com/heyitsdagger/",
                  "https://www.linkedin.com/company/dagger",
                ],
                contactPoint: {
                  "@type": "ContactPoint",
                  contactType: "customer service",
                  url: "https://dagger.agency/we-are-dagger",
                },
              },
            })}
          </script>
        )}
      </Helmet>
      {content && <HeaderComponent />}
      <div className="dagger-case-study">
        {content && (
          <nav className="dagger-breadcrumb">
            <Link
              to="/our-work"
              onClick={(e) => {
                e.preventDefault()
                const category =
                  content.attributes.Category?.data?.attributes?.Category
                const dest = category
                  ? `/our-work?category=${encodeURIComponent(category)}`
                  : "/our-work"
                daggerNavigate(() => navigate(dest))
              }}
            >
              Work
            </Link>
            <span className="dagger-breadcrumb-separator">/</span>
            <span>{content.attributes?.Client}</span>
          </nav>
        )}
        <h1>{content?.attributes?.Title}</h1>
        {content?.attributes?.Subtitle && (
          <h2>{content.attributes?.Subtitle}</h2>
        )}
        {content?.attributes?.Category && (
          <h6>{content.attributes?.Category?.data?.attributes.Category}</h6>
        )}
        <div className="dagger-case-study-content">
          <PixelComponent anchor="bottom-left" className="grid" />
          {content?.attributes.PageContent?.length > 0 &&
            content.attributes.PageContent.map((c) =>
              getContentForComponent(c),
            )}
          {content?.attributes.ShowMoreWork && moreWork}
        </div>
      </div>
      <FooterComponent />
    </div>
  )
}

export default CaseStudy
